import * as THREE from 'three';
import { CollisionWorld } from './CollisionWorld';
import type { MovingPlatform } from './CollisionWorld';
import { Pushable } from '../interact/Pushable';
import { STAGES, type StageDef } from '../stages/stages';
import { clampStageIndex } from '../../shared/protocol';
import { PALETTE, resolveTheme, type StageTheme } from './art/palette';
import {
  PREFABS,
  DECO_SHADOW,
  place,
  mergeInstances,
  expandedPlayBounds,
  scatterGrass,
  type DecoInstance,
  type Placement,
} from './art/prefabs';
import { makeRampPrism, buildArenaBox, buildSkyDome, tint } from './art/arena';

export interface FinishZone {
  x: number;
  z: number;
  r: number;
  needCrate: boolean;
}

export interface WorldHandles {
  scene: THREE.Scene;
  colliders: THREE.Object3D[];
  collisionWorld: CollisionWorld;
  pushables: Pushable[];
  finish: FinishZone[];
  spawnPts: THREE.Vector3[];
}

interface FinishFx {
  mesh: THREE.Mesh;
  vy: number;
  age: number;
  life: number;
}

interface FinishAnim {
  x: number;
  z: number;
  r: number;
  pad: THREE.Mesh;
  ring: THREE.Mesh;
  beam: THREE.Mesh;
  baseY: number;
  fx: FinishFx[];
  spawnAcc: number;
  time: number;
  group: THREE.Group;
}

const ARENA_HEIGHT = 9;
const RAMP_T = 0.5;

export class ThreeSceneManager {
  readonly scene = new THREE.Scene();
  readonly collisionWorld = new CollisionWorld();
  readonly colliders: THREE.Object3D[] = [];
  readonly pushables: Pushable[] = [];
  private finishGroups: THREE.Group[] = [];
  private skyDome: THREE.Mesh | null = null;
  private arenaGroup: THREE.Group | null = null;
  private decoMeshes: THREE.Mesh[] = [];
  private finishAnims: FinishAnim[] = [];
  private waterGroup: THREE.Group | null = null;
  private gateMeshes: THREE.Object3D[] = [];

  /** Platform bergerak stage aktif (untuk chaser/dll). */
  get movingPlatforms(): readonly import('./CollisionWorld').MovingPlatform[] {
    return this.collisionWorld.platformList;
  }

  /** Jumlah gate stage ini (untuk StageRuntime). */
  get gateCount(): number {
    return this.gateMeshes.length;
  }

  /** Buka gate ke-i: nonaktifkan collider + sembunyikan mesh. */
  setGateOpen(index: number): void {
    const mesh = this.gateMeshes[index];
    if (!mesh) return;
    this.collisionWorld.setColliderEnabled(mesh, false);
    mesh.visible = false;
  }

  build(stageIndex = 0, playerCount = 1): WorldHandles {
    this.scene.background = new THREE.Color(PALETTE.skyHorizon);
    this.scene.fog = new THREE.Fog(PALETTE.fog, 45, 120);

    const hemi = new THREE.HemisphereLight(0xeaf6ff, 0xdfece0, 0.85);
    this.scene.add(hemi);
    const soft = new THREE.AmbientLight(0xffffff, 0.4);
    this.scene.add(soft);

    const sun = new THREE.DirectionalLight(0xfff3e0, 1.15);
    sun.position.set(10, 16, 8);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.left = -24;
    sun.shadow.camera.right = 24;
    sun.shadow.camera.top = 24;
    sun.shadow.camera.bottom = -24;
    sun.shadow.bias = -0.0006;
    this.scene.add(sun);

    this.skyDome = buildSkyDome(52);
    this.scene.add(this.skyDome);

    const layout = this.buildStageLayout(stageIndex, playerCount);

    return {
      scene: this.scene,
      colliders: this.colliders,
      collisionWorld: this.collisionWorld,
      pushables: this.pushables,
      finish: layout.finish,
      spawnPts: layout.spawnPts,
    };
  }

  /** Bangun ulang layout stage (dipakai ganti stage tanpa restart scene).
   *  `playerCount`: 1 = single; multiplayer 2-4 menambah crate ekstra
   *  (0 untuk 1P, 1 untuk 2P, semua untuk 3-4P) supaya tiap pemain kebagian. */
  buildStageLayout(index: number, playerCount = 1): { finish: FinishZone[]; spawnPts: THREE.Vector3[] } {
    const def = STAGES[clampStageIndex(index)];
    const theme = resolveTheme(def.theme);

    this.clearStage();

    // Lantai arena (hijau mint). Bila ada sungai cekung selebar arena,
    // lantai dibelah jadi lajur-lajur (parit digali tersendiri di buildWaters).
    const half = def.arena?.half ?? 12;
    this.buildArenaFloor(def, half);
    // Lantai dunia di luar kotak (pucat). Dibangun per-stage agar bisa
    // melubangi area parit (outer statis akan menutupi parit!).
    this.buildOuterGround(def, half);

    let boxI = 0;
    for (const [x, y, z, w, h, d, color] of def.boxes) {
      void color;
      const tall = h >= 1.5;
      const boxColor = tall ? theme.platformAlt : boxI % 2 === 0 ? theme.platform : theme.platformAlt;
      boxI++;
      const mesh = this.collisionWorld.addBox(x, y, z, w, h, d, boxColor) as THREE.Mesh;
      this.styleCollider(mesh, boxColor);
      if (tall) this.addPoleCap(mesh, y, h, theme.accent);
    }
    for (const [x, z, w, d, lowY, highY, color] of def.ramps) {
      void color;
      const mesh = this.collisionWorld.addRamp(x, z, w, d, lowY, highY, theme.ramp) as THREE.Mesh;
      const old = mesh.geometry;
      mesh.geometry = tint(makeRampPrism(w, d, lowY, highY, RAMP_T), theme.ramp);
      old.dispose();
      mesh.position.set(x, 0, z);
      mesh.rotation.set(0, 0, 0);
      this.styleCollider(mesh, theme.ramp);
    }

    // Dinding kotak akrilik (collider tipis tak terlihat) ikut arena.
    this.buildArena(def, theme);

    // Gate solid antar-bagian: collider nyata (tak bisa dilompati/diterobos).
    this.buildGates(def, theme);

    this.scene.add(...this.collisionWorld.meshes);
    this.colliders.length = 0;
    this.colliders.push(...this.collisionWorld.meshes);

    for (const c of def.crates) this.addPushable(c.x, c.z, c.size, theme, c.height);
    const extras = def.mpExtraCrates ?? [];
    const extraCount = playerCount >= 3 ? extras.length : playerCount >= 2 ? Math.min(1, extras.length) : 0;
    for (let i = 0; i < extraCount; i++) {
      const c = extras[i];
      this.addPushable(c.x, c.z, c.size, theme, c.height);
    }
    for (const pl of def.platforms ?? []) {
      const mp = this.collisionWorld.addPlatform(pl);
      this.stylePlatform(mp, theme);
      // Mesh platform HARUS masuk scene (baru setelah collider statis
      // di-add sekali; skip = collider aktif tapi tak terlihat).
      this.scene.add(mp.mesh);
      this.colliders.push(mp.mesh);
    }

    const spawnPts = def.spawns.map(([x, z]) => {
      const s = this.collisionWorld.sampleGroundHeight(x, z);
      return new THREE.Vector3(x, s.valid ? s.height : 0, z);
    });

    const finishes = (def.finishes ?? [def.finish]).map((fd) => this.buildFinish(fd, theme));

    this.buildWaters(def);

    this.buildDecorations(def, half, index);

    return { finish: finishes, spawnPts };
  }

  /** Kosongkan seluruh objek per-stage (collider, crate, finish, arena, deko). */
  private clearStage(): void {
    for (const m of this.collisionWorld.meshes) {
      this.scene.remove(m);
      this.disposeObject(m);
    }
    this.collisionWorld.clear();
    this.colliders.length = 0;

    for (const c of this.pushables) {
      this.scene.remove(c.mesh);
      c.dispose();
    }
    this.pushables.length = 0;

    if (this.finishGroups.length > 0) {
      for (const g of this.finishGroups) {
        this.disposeObject(g);
        this.scene.remove(g);
      }
      this.finishGroups = [];
    }
    this.finishAnims = [];

    if (this.arenaGroup) {
      this.disposeObject(this.arenaGroup);
      this.scene.remove(this.arenaGroup);
      this.arenaGroup = null;
    }

    for (const m of this.decoMeshes) {
      this.scene.remove(m);
      this.disposeObject(m);
    }
    this.decoMeshes = [];

    if (this.waterGroup) {
      this.disposeObject(this.waterGroup);
      this.scene.remove(this.waterGroup);
      this.waterGroup = null;
    }

    this.gateMeshes.length = 0;
  }

  private disposeObject(root: THREE.Object3D): void {
    root.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        const mat = o.material;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else mat.dispose();
      }
    });
  }

  /** Gate solid antar bagian: setinggi 6 m (lewat batas lompat 1.2 m),
   *  selebar corridor + overlap ke bahu canyon supaya tak ada celah.
   *  Visual BIRU TRANSPARAN (energy barrier) — bukan tembok abu —
   *  supaya terbaca sebagai pintu checkpoint; fisika tetap solid. */
  private buildGates(def: StageDef, _theme: StageTheme): void {
    const width = def.corridorW ?? 3.8;
    for (const g of def.gates ?? []) {
      // Tebal 0.6 (bukan 0.3): resolve satu frame (~0.35 maks) tak bisa
      // melempar pemain/crate menembus gate saat terjepit dinding chase.
      const mesh = this.collisionWorld.addBox(0, 3, g.z, width, 6, 0.6, 0x38bdf8) as THREE.Mesh;
      mesh.material = new THREE.MeshStandardMaterial({
        color: 0x38bdf8,
        emissive: 0x1d6fd1,
        emissiveIntensity: 0.55,
        roughness: 0.25,
        transparent: true,
        opacity: 0.45,
        depthWrite: false,
      });
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.name = `Gate@${g.z}:${g.require}`;
      mesh.userData.occluder = true;
      this.gateMeshes.push(mesh);
    }
  }

  /** Material low-poly flat shading + warna tema. */
  private styleCollider(mesh: THREE.Mesh, color: number): void {
    mesh.material = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.85,
      flatShading: true,
    });
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    // Penanda occluder untuk fade lingkaran AIM (AimFade).
    mesh.userData.occluder = true;
  }

  /** Kap kerucut aksen untuk tiang/kotak tinggi (dekorasi, visual saja). */
  private addPoleCap(mesh: THREE.Mesh, y: number, h: number, color: number): void {
    const cap = new THREE.Mesh(
      new THREE.ConeGeometry(0.42, 0.34, 6),
      new THREE.MeshStandardMaterial({ color, roughness: 0.7, flatShading: true, emissive: color, emissiveIntensity: 0.18 }),
    );
    cap.position.y = y + h / 2 + 0.17;
    cap.castShadow = true;
    mesh.add(cap);
  }

  /** Platform bergerak: slab tema + pita aksen di atas (baca arah gerak). */
  private stylePlatform(mp: MovingPlatform, theme: StageTheme): void {
    const mesh = mp.mesh as THREE.Mesh;
    mesh.material = new THREE.MeshStandardMaterial({
      color: theme.platformAlt,
      roughness: 0.8,
      flatShading: true,
    });
    // Dinding chase mulai sebagai GHOST (tembus pandang + non-solid
    // sampai runtime menyalakannya): tak ada jalan buntu saat diam.
    if (mp.spec.mode === 'chase') {
      const mat = mesh.material as THREE.MeshStandardMaterial;
      mat.transparent = true;
      mat.opacity = 0.35;
    }
    const { w, h, d } = mp.spec;
    const stripeMat = new THREE.MeshStandardMaterial({
      color: theme.accent,
      emissive: theme.accent,
      emissiveIntensity: 0.3,
      roughness: 0.6,
      flatShading: true,
    });
    const stripe = new THREE.Mesh(new THREE.BoxGeometry(w * 0.94, 0.05, d * 0.94), stripeMat);
    stripe.position.y = h / 2 - 0.04;
    stripe.castShadow = false;
    mesh.add(stripe);
    if (mp.spec.axis === 'z') {
      const arrow = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.22, 4), stripeMat);
      arrow.rotation.x = -Math.PI / 2;
      arrow.position.set(0, h / 2 + 0.05, d * 0.28);
      mesh.add(arrow);
    }
  }

  /** Dinding kotak akrilik: collider tipis tak terlihat yang WAJAH DALAMNYA
   *  TEPAT di bidang panel art (±half). Karakter menyentuh dinding visual,
   *  dan jejak dinding menempel di permukaan art (tidak melayang). */
  private buildArena(def: StageDef, theme: StageTheme): void {
    const half = def.arena?.half ?? 12;
    const wallColor = theme.ramp;
    const visInvis = 0.45;
    // Pusat collider = half + 0.225 (di BALIK panel art): wajah dalam
    // (min/max menghadap arena) jatuh tepat di half = bidang texture.
    const wallCenter = half + 0.225;
    const walls: Array<[number, number, number, number]> = [
      [0, -wallCenter, half * 2, visInvis],
      [0, wallCenter, half * 2, visInvis],
      [-wallCenter, 0, visInvis, half * 2],
      [wallCenter, 0, visInvis, half * 2],
    ];
    for (const [x, z, lw, ld] of walls) {
      const mesh = this.collisionWorld.addBox(x, ARENA_HEIGHT / 2, z, lw, ARENA_HEIGHT, ld, wallColor) as THREE.Mesh;
      const mat = new THREE.MeshStandardMaterial({
        color: wallColor,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      mesh.material = mat;
    }

    const arena = buildArenaBox(half, ARENA_HEIGHT);
    this.scene.add(arena);
    this.arenaGroup = arena;
  }

  /** Lantai dunia di luar kotak akrilik: 4 persegi di sekeliling arena.
   *  (Bukan satu bidang 60x60: bidang utuh akan menutupi lubang parit.) */
  private buildOuterGround(def: StageDef, half: number): void {
    void def;
    const mat = new THREE.MeshStandardMaterial({
      color: PALETTE.groundOuter,
      roughness: 0.95,
    });
    const rect = (x0: number, x1: number, z0: number, z1: number): void => {
      if (x1 - x0 < 1e-6 || z1 - z0 < 1e-6) return;
      const m = new THREE.Mesh(new THREE.PlaneGeometry(x1 - x0, z1 - z0), mat);
      m.rotation.x = -Math.PI / 2;
      m.position.set((x0 + x1) / 2, 0, (z0 + z1) / 2);
      m.receiveShadow = true;
      this.collisionWorld.meshes.push(m);
    };
    const E = 30;
    rect(-E, E, half, E);
    rect(-E, E, -E, -half);
    rect(half, E, -half, half);
    rect(-E, -half, -half, half);
  }

  /** Lantai arena: satu bidang, atau lajur-lajur bila ada parit selebar arena. */
  private buildArenaFloor(def: StageDef, half: number): void {    const floorMat = new THREE.MeshStandardMaterial({
      color: PALETTE.grassMint,
      roughness: 0.95,
    });
    const strip = (z0: number, z1: number): void => {
      if (z1 - z0 < 1e-6) return;
      const geo = new THREE.PlaneGeometry(half * 2, z1 - z0);
      const m = new THREE.Mesh(geo, floorMat);
      m.rotation.x = -Math.PI / 2;
      m.position.set(0, 0.002, (z0 + z1) / 2);
      m.receiveShadow = true;
      this.collisionWorld.meshes.push(m);
    };
    const full = (def.waters ?? []).filter(
      (wr) =>
        wr.bed !== undefined &&
        wr.x - wr.w / 2 <= -half + 0.01 &&
        wr.x + wr.w / 2 >= half - 0.01,
    );
    if (full.length === 0) {
      strip(-half, half);
      return;
    }
    const cuts = [-half, half];
    for (const wr of full) {
      cuts.push(
        Math.max(-half, wr.z - wr.d / 2),
        Math.min(half, wr.z + wr.d / 2),
      );
    }
    cuts.sort((a, b) => a - b);
    for (let i = 0; i + 1 < cuts.length; i++) {
      const a = cuts[i];
      const b = cuts[i + 1];
      if (b - a < 1e-6) continue;
      const mid = (a + b) / 2;
      const inPit = full.some((wr) => mid > wr.z - wr.d / 2 && mid < wr.z + wr.d / 2);
      if (!inPit) strip(a, b);
    }
  }

  /** Sungai: parit cekung (bila `bed` ada: dinding parit + dasar dalam +
   *  permukaan air) atau genangan dangkal. Visual saja kecuali pit yang
   *  didaftarkan ke fisika; crate terparkir di dasar parit. */
  private buildWaters(def: StageDef): void {
    const waters = def.waters ?? [];
    if (waters.length === 0) return;
    const group = new THREE.Group();
    for (const wr of waters) {
      this.collisionWorld.addWater(wr.x, wr.z, wr.w, wr.d, wr.y);
      const bedY = wr.bed ?? wr.y - 0.07;
      if (wr.bed !== undefined) {
        // Pit di-inset 0.2 m dari bibir air: strip atas dinding (tanah)
        // adalah DARATAN betulan, bukan jebakan visual. Fisika = visual.
        this.collisionWorld.addPit(wr.x, wr.z, wr.w, wr.d - 0.4, wr.bed);
        // Dinding parit (visual saja, BUKAN collider: crate harus bisa
        // didorong masuk; pemain yang jatuh langsung respawn via aturan air).
        // Tipis (0.2) dan tepat di DALAM batas parit: tak menimpa lantai.
        const wallMat = new THREE.MeshStandardMaterial({ color: 0xc7ad87, roughness: 0.9 });
        for (const sgn of [-1, 1]) {
          const wall = new THREE.Mesh(new THREE.BoxGeometry(wr.w, 0 - wr.bed, 0.2), wallMat);
          wall.position.set(wr.x, wr.bed + (0 - wr.bed) / 2 - 0.01, wr.z + sgn * (wr.d / 2 - 0.1));
          group.add(wall);
        }
      }
      const bed = new THREE.Mesh(
        new THREE.PlaneGeometry(wr.w, wr.d),
        new THREE.MeshStandardMaterial({ color: 0x4f9aa8, roughness: 0.9 }),
      );
      bed.rotation.x = -Math.PI / 2;
      bed.position.set(wr.x, bedY, wr.z);
      bed.receiveShadow = true;
      group.add(bed);
      const surface = new THREE.Mesh(
        new THREE.PlaneGeometry(wr.w, wr.d),
        new THREE.MeshStandardMaterial({
          color: 0x3aa7dc,
          transparent: true,
          opacity: 0.6,
          roughness: 0.1,
        }),
      );
      surface.rotation.x = -Math.PI / 2;
      surface.position.set(wr.x, wr.y, wr.z);
      group.add(surface);
      const foamMat = new THREE.MeshStandardMaterial({ color: 0xf2f7f5, roughness: 0.8 });
      for (const sgn of [-1, 1]) {
        const foam = new THREE.Mesh(new THREE.BoxGeometry(wr.w - 0.6, 0.03, 0.18), foamMat);
        foam.position.set(wr.x, wr.y + 0.035, wr.z + sgn * (wr.d / 2 - 0.35));
        group.add(foam);
      }
    }
    this.scene.add(group);
    this.waterGroup = group;
  }

  /** Pad + cincin + pilar cahaya + efek terpasang (visual saja). */
  private buildFinish(
    fd: { x: number; z: number; r: number; needCrate: boolean },
    theme: StageTheme,
  ): FinishZone {
    const { x, z, r } = fd;
    const s = this.collisionWorld.sampleGroundHeight(x, z);
    const gy = s.valid ? s.height : 0;
    const group = new THREE.Group();

    const pad = new THREE.Mesh(
      new THREE.CylinderGeometry(r, r, 0.07, 26),
      new THREE.MeshStandardMaterial({
        color: theme.accent,
        emissive: theme.accent,
        emissiveIntensity: 0.42,
        roughness: 0.45,
        flatShading: true,
      }),
    );
    pad.position.set(x, gy + 0.035, z);
    pad.receiveShadow = true;
    group.add(pad);

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(r * 1.14, 0.045, 6, 26),
      new THREE.MeshStandardMaterial({
        color: theme.accent,
        emissive: theme.accent,
        emissiveIntensity: 0.6,
        roughness: 0.4,
        flatShading: true,
      }),
    );
    ring.position.set(x, gy + 0.02, z);
    ring.rotation.x = Math.PI / 2;
    group.add(ring);

    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(r * 0.3, r * 0.3, 6, 14, 1, true),
      new THREE.MeshBasicMaterial({
        color: theme.accent,
        transparent: true,
        opacity: 0.15,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    beam.position.set(x, gy + 3, z);
    group.add(beam);

    this.scene.add(group);
    this.finishGroups.push(group);
    this.finishAnims.push({
      x,
      z,
      r,
      pad,
      ring,
      beam,
      baseY: gy,
      fx: [],
      spawnAcc: 0,
      time: 0,
      group,
    });
    return { x, z, r, needCrate: fd.needCrate };
  }

  /** Indikator "crate terpasang benar": pad & cincin menyala + partikel
   *  pastel kecil melayang naik (lembut, bukan kedipan kontras). Satu
   *  indikator per pad finish (multi-finish: tiap pad idealnya diisi). */
  updateFinishVisual(pushables: Pushable[], delta: number): void {
    if (this.finishAnims.length === 0) return;
    for (const f of this.finishAnims) {
      f.time += delta;
      const t = f.time;
      const installed = pushables.some(
        (c) => Math.hypot(c.pos.x - f.x, c.pos.z - f.z) < f.r,
      );

    const padMat = f.pad.material as THREE.MeshStandardMaterial;
    const ringMat = f.ring.material as THREE.MeshStandardMaterial;
    const beamMat = f.beam.material as THREE.MeshBasicMaterial;
    const pulse = installed ? 0.09 * Math.sin(t * 5) : 0.02 * Math.sin(t * 2.2);
    const k = installed ? 1 : 0.22;
    padMat.emissiveIntensity = 0.5 + k * 0.5 + pulse * k;
    f.pad.scale.setScalar(1 + (installed ? 0.04 : 0.0) + pulse * 0.25);
    ringMat.emissiveIntensity = 0.6 + k * 0.4 * (1 + Math.sin(t * 5) * 0.3);
    f.ring.position.y = f.baseY + 0.02 + (installed ? 0.09 : 0) + pulse * 0.05;
    beamMat.opacity = 0.15 + k * 0.14;

    if (installed) {
      f.spawnAcc += delta;
      if (f.spawnAcc > 0.14 && f.fx.length < 12) {
        f.spawnAcc = 0;
        const ang = t * 1.7;
        const rr = f.r * (0.82 + 0.2 * Math.abs(Math.sin(t * 3.1)));
        const p = new THREE.Mesh(
          new THREE.SphereGeometry(0.05, 5, 4),
          new THREE.MeshBasicMaterial({
            color: 0xfff6e0,
            transparent: true,
            opacity: 0.9,
            depthWrite: false,
          }),
        );
        p.position.set(
          f.x + Math.cos(ang) * rr,
          f.baseY + 0.1,
          f.z + Math.sin(ang) * rr,
        );
        f.group.add(p);
        f.fx.push({ mesh: p, vy: 0.5 + 0.3 * Math.abs(Math.sin(t * 2.3)), age: 0, life: 1.05 });
      }
    }

    f.fx = f.fx.filter((fx) => {
      fx.age += delta;
      const mat = fx.mesh.material as THREE.MeshBasicMaterial;
      if (fx.age >= fx.life) {
        mat.dispose();
        f.group.remove(fx.mesh);
        return false;
      }
      fx.mesh.position.y += fx.vy * delta;
      mat.opacity = Math.max(0, 0.9 * (1 - fx.age / fx.life));
      return true;
    });
    }
  }

  /** Crate tema: badan pastel + band aksen + rim atas. */
  private addPushable(x: number, z: number, size: number, theme: StageTheme, height = 1.2): void {
    const s = this.collisionWorld.sampleGroundHeight(x, z);
    const crate = new Pushable(x, s.valid ? s.height : 0, z, size, height, theme.crate);
    const bodyMat = crate.mesh.material as THREE.MeshStandardMaterial;
    bodyMat.color.setHex(theme.crate);
    bodyMat.roughness = 0.8;
    bodyMat.flatShading = true;

    const bandMat = new THREE.MeshStandardMaterial({
      color: theme.crateAccent,
      roughness: 0.65,
      flatShading: true,
      emissive: theme.crateAccent,
      emissiveIntensity: 0.14,
    });
    const half = size / 2;
    const bandH = 0.1;
    const bandT = 0.02;
    const bY = 0.12;
    const side = (sx: number, sz: number, lw: number, ld: number): void => {
      const b = new THREE.Mesh(
        new THREE.BoxGeometry(sx === 0 ? lw : bandT, bandH, sx === 0 ? bandT : ld),
        bandMat,
      );
      b.position.set(sx, bY, sz);
      b.castShadow = false;
      crate.mesh.add(b);
    };
    side(half + bandT / 2, 0, bandT, size + 0.04);
    side(-(half + bandT / 2), 0, bandT, size + 0.04);
    side(0, half + bandT / 2, size + 0.04, bandT);
    side(0, -(half + bandT / 2), size + 0.04, bandT);

    const rim = new THREE.Mesh(
      new THREE.BoxGeometry(size + 0.05, 0.035, size + 0.05),
      bandMat,
    );
    rim.position.y = crate.height / 2 - 0.06;
    rim.castShadow = false;
    crate.mesh.add(rim);

    this.scene.add(crate.mesh);
    this.pushables.push(crate);
  }

  /** Pohon/rumput/batu/bunga: eksplisit dari def.deco + scatter rumput
   *  deterministik di zona aman. Digabung jadi 2 mesh (cast + tanpa cast). */
  private buildDecorations(def: StageDef, arenaHalf: number, index: number): void {
    const gameplay: Array<{ minX: number; maxX: number; minZ: number; maxZ: number }> = [];
    for (const [x, y, z, w, h, d] of def.boxes) {
      void y;
      void h;
      gameplay.push({ minX: x - w / 2, maxX: x + w / 2, minZ: z - d / 2, maxZ: z + d / 2 });
    }
    for (const [x, z, w, d] of def.ramps) {
      gameplay.push({ minX: x - w / 2, maxX: x + w / 2, minZ: z - d / 2, maxZ: z + d / 2 });
    }
    for (const c of def.crates) {
      gameplay.push({ minX: c.x - c.size / 2, maxX: c.x + c.size / 2, minZ: c.z - c.size / 2, maxZ: c.z + c.size / 2 });
    }
    for (const [x, z] of def.spawns) {
      gameplay.push({ minX: x - 1.2, maxX: x + 1.2, minZ: z - 1.2, maxZ: z + 1.2 });
    }
    gameplay.push({
      minX: def.finish.x - def.finish.r,
      maxX: def.finish.x + def.finish.r,
      minZ: def.finish.z - def.finish.r,
      maxZ: def.finish.z + def.finish.r,
    });
    for (const wr of def.waters ?? []) {
      gameplay.push({
        minX: wr.x - wr.w / 2,
        maxX: wr.x + wr.w / 2,
        minZ: wr.z - wr.d / 2,
        maxZ: wr.z + wr.d / 2,
      });
    }
    const safe = expandedPlayBounds(gameplay, 1.8);

    const instances: DecoInstance[] = [...(def.deco ?? [])];
    const grassCount = Math.min(36, Math.max(20, Math.round(arenaHalf * 2.4)));
    instances.push(...scatterGrass(arenaHalf, safe, grassCount, 1300 + index * 97));

    const castList: Placement[] = [];
    const noCastList: Placement[] = [];
    for (const it of instances) {
      const geo = PREFABS[it.kind];
      if (!geo) continue;
      const s = this.collisionWorld.sampleGroundHeight(it.x, it.z);
      const y = s.valid && s.height >= 0 ? s.height : 0;
      const list = DECO_SHADOW[it.kind] ? castList : noCastList;
      list.push(place(geo, it.x, y, it.z, it.a ?? 0, it.s ?? 1));
    }

    if (castList.length > 0) {
      const geo = mergeInstances(castList);
      const mat = new THREE.MeshStandardMaterial({
        vertexColors: true,
        flatShading: true,
        roughness: 0.85,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.scene.add(mesh);
      this.decoMeshes.push(mesh);
    }
    if (noCastList.length > 0) {
      const geo = mergeInstances(noCastList);
      const mat = new THREE.MeshStandardMaterial({
        vertexColors: true,
        flatShading: true,
        roughness: 0.9,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.castShadow = false;
      mesh.receiveShadow = true;
      this.scene.add(mesh);
      this.decoMeshes.push(mesh);
    }
  }
}